/**
 * RiskMetricCard - Component for displaying individual risk metrics
 * Neutral card design — white background, numbers colored red/green only
 *
 * TASK-872: the colour used to come from the caller's `status` bucket while the caption came
 * from `threshold`, so one card contradicted itself — `Volatility 11.55%` rendered red above
 * its own line saying the 20.00% threshold was NOT exceeded (`RiskTab` buckets anything over
 * 10 as `warning`, and `warning` was mapped to the same red as `critical`). Both halves now
 * read the same threshold through `metricFormat`, and `value` accepts null so an unmeasured
 * metric renders `—` instead of a coerced `0.00%` in colour.
 */

import React from 'react';

import {
  MetricTone,
  TONE_TEXT_CLASS,
  exceedsThreshold,
  formatMetric,
  isAbsent,
  toneForThreshold,
} from '../../../utils/metricFormat';

export interface RiskMetricCardProps {
  metric: string;
  /** Null when the metric was not computed — rendered `—`, never `0`. */
  value: number | null | undefined;
  threshold?: number;
  status?: 'safe' | 'warning' | 'critical';
  format?: 'percentage' | 'currency' | 'number';
  currency?: string;
  description?: string;
  icon?: React.ReactNode;
  className?: string;
  /**
   * The threshold is a FLOOR, not a ceiling — for metrics where higher is better (Sharpe).
   * Without it a Sharpe of 1.4 against a threshold of 1 would colour as a breach.
   */
  higherIsBetter?: boolean;
}

const RiskMetricCard: React.FC<RiskMetricCardProps> = ({
  metric,
  value,
  threshold,
  status,
  format = 'percentage',
  currency = 'CHF',
  description,
  className = '',
  higherIsBetter = false,
}) => {
  const kind = format === 'percentage' ? 'percent' : format === 'currency' ? 'currency' : 'number';
  const show = (v: number | null | undefined) =>
    formatMetric(v, { kind, currency, decimals: kind === 'currency' ? 0 : 2 });

  // The threshold decides the colour when there is one. `status` is only consulted as a
  // fallback for cards that pass no threshold — it is a caller heuristic, and on the card that
  // reported this defect it disagreed with the number printed underneath it.
  let tone: MetricTone;
  if (isAbsent(value)) {
    tone = 'absent';
  } else if (threshold !== undefined) {
    tone = toneForThreshold(value, threshold, { reverse: higherIsBetter });
  } else if (status === 'critical' || status === 'warning') {
    tone = 'negative';
  } else if (status === 'safe') {
    tone = 'positive';
  } else {
    tone = 'neutral';
  }

  const isExceeded = exceedsThreshold(value, threshold, { reverse: higherIsBetter });

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-5 ${className}`}>
      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">{metric}</h4>
      <p className={`text-2xl font-bold mt-1 ${TONE_TEXT_CLASS[tone]}`}>
        {show(value)}
      </p>

      {description && (
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      )}

      {threshold !== undefined && (
        <div className="mt-2 text-xs text-gray-500">
          Threshold: <span className={isExceeded ? 'text-red-600 font-medium' : 'text-gray-700'}>{show(threshold)}</span>
          {isExceeded && <span className="text-red-600 font-medium ml-1">Exceeded</span>}
        </div>
      )}
    </div>
  );
};

export default RiskMetricCard;
